import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://giftbox.mobi';
const DEFAULT_TITLE = 'GiftBox — Digital Gift Cards, Vouchers & Top-ups';
const DEFAULT_DESC =
  'Shop 17,000+ e-vouchers, gift cards, and mobile top-ups from top brands. Instant delivery and secure checkout.';

const ROUTE_SEO = {
  '/': {
    title: 'GiftBox — Digital Gift Cards, Vouchers & Top-ups',
    description: DEFAULT_DESC,
  },
  '/products': {
    title: 'Gift Cards & Vouchers — Browse Products | GiftBox',
    description:
      'Browse digital gift cards, gaming vouchers, entertainment credits, and mobile top-ups. Filter by brand, country, and price.',
  },
  '/contact': {
    title: 'Contact Us | GiftBox',
    description: 'Get help with GiftBox orders and vouchers. Contact our support team.',
  },
  '/cart': {
    title: 'Your Cart | GiftBox',
    description: 'Review your GiftBox cart and proceed to secure checkout.',
    noindex: true,
  },
  '/checkout': {
    title: 'Checkout | GiftBox',
    description: 'Complete your GiftBox purchase securely.',
    noindex: true,
  },
  '/signin': {
    title: 'Sign In | GiftBox',
    description: 'Sign in to your GiftBox account to manage orders.',
    noindex: true,
  },
  '/signup': {
    title: 'Create Account | GiftBox',
    description: 'Create a GiftBox account to buy vouchers.',
  },
  '/privacyPolicy': {
    title: 'Privacy Policy | GiftBox',
    description: 'How GiftBox collects, uses, and protects your personal information.',
  },
  '/Terms&Conditions': {
    title: 'Terms & Conditions | GiftBox',
    description:
      'Terms for buying digital gift cards, vouchers, and mobile top-ups on GiftBox, including delivery, cancellation, and refund terms.',
  },
  '/RefundPolicy': {
    title: 'Refund & Cancellation Policy | GiftBox',
    description:
      'How to cancel an order, when you can claim a refund on digital vouchers and top-ups, and how long refunds take.',
  },
  '/profile/history': {
    title: 'Order History | GiftBox',
    description: 'View your GiftBox purchase history.',
    noindex: true,
  },
  '/profile/settings': {
    title: 'Account Settings | GiftBox',
    description: 'Manage your GiftBox account settings.',
    noindex: true,
  },
  '/settings': {
    title: 'Settings | GiftBox',
    description: 'Account, purchase history, support, and policies for GiftBox.',
    noindex: true,
  },
  '/thankyou': {
    title: 'Thank You | GiftBox',
    description: 'Your GiftBox order confirmation.',
    noindex: true,
  },
  '/fail': {
    title: 'Payment Failed | GiftBox',
    noindex: true,
  },
};

function upsertMeta(attr, key, content) {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function resolveSeo(pathname) {
  if (ROUTE_SEO[pathname]) return ROUTE_SEO[pathname];
  if (pathname.startsWith('/products')) {
    return {
      title: 'Product | GiftBox',
      description: 'View gift card and voucher details on GiftBox.',
    };
  }
  return { title: DEFAULT_TITLE, description: DEFAULT_DESC };
}

/**
 * Client-side SEO: title, description, canonical, Open Graph, Twitter, robots.
 * Mount once inside the router.
 */
export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = resolveSeo(pathname);
    const title = seo.title || DEFAULT_TITLE;
    const description = seo.description || DEFAULT_DESC;
    const canonical = `${SITE_URL}${pathname === '/' ? '' : pathname}`;

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', seo.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'GiftBox');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', `${SITE_URL}/logo512.png`);
    upsertMeta('property', 'og:locale', 'en_US');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', `${SITE_URL}/logo512.png`);
  }, [pathname]);

  return null;
}
